import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle, ClipboardList, FileSignature, Mail, MessageCircle, Phone, RefreshCw, XCircle } from 'lucide-react';
import { getExtraLabel, formatExtraValue } from '../utils/patientExtraData';

// Detalle de un turno: una sola versión para la agenda de la computadora
// (CalendarAgendaView) y la del celular (MobileMonthCalendar), que tenían
// cada una su copia con cosas distintas (oct-2026). El diseño es el del
// celular de antes (el que prefiere Esteban): encabezado de color, datos en
// tarjetas y botones grandes uno abajo del otro. Solo dibuja: cada pantalla le
// pasa sus acciones y conserva su lógica. Lo que no recibe, no lo muestra.

const ESTADOS: Record<string, { texto: string; estilo: string }> = {
  pending: { texto: 'Pendiente', estilo: 'bg-amber-100 text-amber-700' },
  confirmed: { texto: 'Confirmado', estilo: 'bg-emerald-100 text-emerald-700' },
  completed: { texto: 'Completado', estilo: 'bg-sky-100 text-sky-700' },
  cancelled: { texto: 'Cancelado', estilo: 'bg-red-100 text-red-600' },
  no_show: { texto: 'No asistió', estilo: 'bg-gray-100 text-gray-600' },
};

const GRADIENTES = {
  salud: 'from-blue-500 via-cyan-500 to-green-500',
  belleza: 'from-pink-500 to-purple-500',
};

const fecha = (valor: string) => {
  const [a, m, d] = String(valor).substring(0, 10).split('-').map(Number);
  return new Date(a, m - 1, d).toLocaleDateString('es-AR');
};

export interface DetalleTurnoProps {
  turno: any;
  servicio: string;
  precio: string;
  onCerrar: () => void;
  notaDelTurno: React.ReactNode;
  notificaciones: React.ReactNode;
  notasPrivadas?: React.ReactNode;
  onWhatsApp?: () => void;
  /** Rubro: define el color del encabezado */
  tipo?: 'salud' | 'belleza';
  /** Historia clínica y receta con los datos del turno */
  clinica?: { historia: boolean; receta: boolean; puedeEmitir: boolean };
  acciones: {
    cargando: boolean;
    onConfirmar?: () => void;
    onCompletar?: () => void;
    onReagendar?: () => void;
    onCancelar?: () => void;
    onBorrar?: () => void;
    onSobreturno?: () => void;
  };
}

const Dato: React.FC<{ etiqueta: string; children: React.ReactNode; ancho?: boolean }> = ({ etiqueta, children, ancho }) => (
  <div className={`bg-gray-50 p-3 rounded-lg ${ancho ? 'col-span-2' : ''}`}>
    <p className="text-sm text-gray-600">{etiqueta}</p>
    <p className="font-medium text-gray-900">{children}</p>
  </div>
);

const DetalleTurno: React.FC<DetalleTurnoProps> = ({
  turno, servicio, precio, onCerrar, notaDelTurno, notificaciones, notasPrivadas, onWhatsApp, tipo = 'salud', clinica, acciones
}) => {
  const navigate = useNavigate();
  const estado = ESTADOS[turno.status] || { texto: turno.status, estilo: 'bg-gray-100 text-gray-600' };
  const hora = String(turno.appointment_time || '').substring(0, 5);
  const activo = turno.status !== 'cancelled' && turno.status !== 'completed';
  const extra = turno.patient_extra_data && typeof turno.patient_extra_data === 'object' ? turno.patient_extra_data : null;
  const extras = extra
    ? Object.entries(extra).filter(([k, v]) => v && !['additional_service_ids', 'all_service_names'].includes(k))
    : [];

  const abrirHistoria = () => {
    const params = new URLSearchParams();
    params.set('patient', turno.client_display_name || '');
    if (extra) {
      if (extra.dni) params.set('dni', String(extra.dni));
      if (Object.keys(extra).length > 0) sessionStorage.setItem('appointmentExtraData', JSON.stringify(extra));
    }
    if (turno.client_email) params.set('email', turno.client_email);
    if (turno.client_phone) params.set('phone', turno.client_phone);
    params.set('fromAppointment', 'true');
    navigate(`/professional/clinical-records/new?${params.toString()}`);
  };
  const abrirReceta = () => {
    const params = new URLSearchParams({ nuevaReceta: '1' });
    params.set('nombre', turno.client_display_name || '');
    if (extra?.dni) params.set('dni', String(extra.dni));
    if (extra?.sexo) params.set('sexo', String(extra.sexo));
    if (extra?.fecha_nacimiento) params.set('nacimiento', String(extra.fecha_nacimiento));
    // Sin matrícula el botón igual se ve y lleva a cargarla (el camino que queremos)
    navigate(clinica?.puedeEmitir ? `/professional/prescriptions?${params.toString()}` : '/professional/habilitar-recetas');
  };

  const boton = 'w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors';

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center bg-black/50 md:p-4" onClick={onCerrar}>
      <style>{`
        @keyframes detalle-turno-subir { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes detalle-turno-aparecer { from { opacity: 0; transform: scale(.97); } to { opacity: 1; transform: scale(1); } }
        .detalle-turno { animation: detalle-turno-subir .3s ease-out; }
        @media (min-width: 768px) { .detalle-turno { animation: detalle-turno-aparecer .2s ease-out; } }
        @media (prefers-reduced-motion: reduce) { .detalle-turno { animation: none; } }
      `}</style>
      <div
        role="dialog"
        aria-label={`Turno de ${turno.client_display_name}`}
        className="detalle-turno w-full md:max-w-lg max-h-[92dvh] md:max-h-[88dvh] overflow-y-auto rounded-t-2xl md:rounded-2xl bg-white text-left shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`p-6 text-white bg-gradient-to-r ${GRADIENTES[tipo] || GRADIENTES.salud}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Detalle del Turno</h2>
            <button type="button" onClick={onCerrar} aria-label="Cerrar" className="text-white text-2xl leading-none">×</button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{turno.client_display_name}</h3>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${estado.estilo}`}>{estado.texto}</span>
              <span className="text-sm text-gray-600">{servicio}</span>
              {turno.is_first_time && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">Primera vez</span>
              )}
              {turno.is_rescheduled && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                  <RefreshCw className="w-3 h-3 inline" /> Reagendado
                </span>
              )}
            </div>
          </div>

          {turno.is_rescheduled && turno.rescheduled_from && (
            <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
              <RefreshCw className="w-4 h-4 inline" /> Reagendado desde: <span className="font-semibold">{turno.rescheduled_from}</span>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Dato etiqueta="Fecha">{fecha(turno.appointment_date)}</Dato>
              <Dato etiqueta="Hora">{hora}</Dato>
              <Dato etiqueta="Duración">{turno.duration} min</Dato>
              <Dato etiqueta="Precio">{precio}</Dato>
              {turno.location_name && (
                <Dato etiqueta="Sede" ancho>{turno.location_name}{turno.location_city ? ` · ${turno.location_city}` : ''}</Dato>
              )}
            </div>

            {(turno.client_phone || turno.client_email) && (
              <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                {turno.client_phone && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="mr-auto flex items-center gap-2 font-medium text-gray-900 whitespace-nowrap"><Phone className="w-4 h-4 text-gray-500 shrink-0" />{turno.client_phone}</span>
                    {onWhatsApp && (
                      <button type="button" onClick={onWhatsApp} className="px-3 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-medium flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                      </button>
                    )}
                    <a href={`tel:${String(turno.client_phone).replace(/[^\d+]/g, '')}`} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-100">Llamar</a>
                  </div>
                )}
                {turno.client_email && (
                  <a href={`mailto:${turno.client_email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:underline break-all"><Mail className="w-4 h-4 text-gray-500 shrink-0" />{turno.client_email}</a>
                )}
              </div>
            )}

            <div>
              <p className="text-sm text-gray-600 mb-1">Nota del turno:</p>
              {notaDelTurno}
            </div>

            {notasPrivadas && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Notas privadas:</p>
                {notasPrivadas}
              </div>
            )}

            {extras.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Datos adicionales del paciente:</p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                  {extras.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 text-sm">
                      <span className="font-bold text-gray-700">{getExtraLabel(k)}:</span>
                      <span className="text-gray-900 text-right">{formatExtraValue(k, v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Bell className="w-4 h-4" /> Notificaciones
              </h4>
              {notificaciones}
            </div>
          </div>

          {acciones.onSobreturno && turno.status !== 'cancelled' && (
            <button
              type="button"
              onClick={acciones.onSobreturno}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 font-medium transition-colors"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold">+ST</span>
              Agregar sobreturno a las {hora}
            </button>
          )}

          <div className="mt-6 space-y-3">
            {activo && (
              <>
                {acciones.onConfirmar && turno.status === 'pending' && (
                  <button type="button" onClick={acciones.onConfirmar} disabled={acciones.cargando} className={`${boton} bg-green-500 hover:bg-green-600 text-white`}>
                    <CheckCircle className="w-5 h-5" /> Confirmar Turno
                  </button>
                )}
                {acciones.onCompletar && (
                  <button type="button" onClick={acciones.onCompletar} disabled={acciones.cargando} className={`${boton} bg-blue-500 hover:bg-blue-600 text-white`}>
                    <CheckCircle className="w-5 h-5" /> Marcar como Completado
                  </button>
                )}
                {acciones.onReagendar && (
                  <button type="button" onClick={acciones.onReagendar} disabled={acciones.cargando} className={`${boton} bg-amber-100 hover:bg-amber-200 text-amber-700`}>
                    <RefreshCw className="w-5 h-5" /> Reagendar Turno
                  </button>
                )}
                {acciones.onCancelar && (
                  <button type="button" onClick={acciones.onCancelar} disabled={acciones.cargando} className={`${boton} bg-red-100 hover:bg-red-200 text-red-700`}>
                    <XCircle className="w-5 h-5" /> Cancelar Turno
                  </button>
                )}
              </>
            )}

            {clinica?.historia && (
              <button type="button" onClick={abrirHistoria} className={`${boton} font-bold bg-purple-100 hover:bg-purple-200 text-purple-700`}>
                <ClipboardList className="w-5 h-5" /> Crear Historia Clínica
              </button>
            )}
            {clinica?.receta && (
              <button type="button" onClick={abrirReceta} className={`${boton} font-bold bg-teal-100 hover:bg-teal-200 text-teal-800`}>
                <FileSignature className="w-5 h-5" /> Crear Receta
              </button>
            )}

            {turno.status === 'cancelled' && acciones.onBorrar && (
              <button type="button" onClick={acciones.onBorrar} disabled={acciones.cargando} className={`${boton} bg-red-50 hover:bg-red-100 text-red-700 border border-red-200`}>
                <XCircle className="w-5 h-5" /> Borrar de la agenda
              </button>
            )}

            <button type="button" onClick={onCerrar} className={`${boton} bg-gray-200 hover:bg-gray-300 text-gray-700`}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DetalleTurno;
